import { ChannelRepository, FileRepository, MessageContentType, MessageRepository, UserRepository } from "@amityco/ts-sdk";
import api from ".";
import { getUsersFleetRio } from "./fleets";

const VOLKINHO_AVATAR_URL = 'https://non-prod.latam-uptime-gateway.rio.cloud/assets/images/volkinho.svg';

export interface IChannel {
  id: string;
  displayName: string;
  lastActivity: Date;
  hasUnreadMessage: boolean;
  members: IMember[];
  profileImage?: string;
  isPackageChannel?: boolean;
}

export interface IMember {
  type?: string;
  userId: string;
  displayName: string;
  profileImage?: string;
}

export const createChannel = async (members: IMember[], ownerId: string): Promise<IChannel> => {
  const createdChannel = await ChannelRepository.createChannel({
    displayName: `${ownerId}-len-${members.length + 1}`,
    type: "community",
    userIds: [...members.map((member) => member.userId), ownerId],
    metadata: {
      deletedUsers: [],
      group: false,
      users: [...members.map((member) => member.userId), ownerId],
    },
    tags: ["tag"],
  });

  return {
    id: createdChannel.data._id,
    displayName: members.map((member) => member.displayName).join(", "),
    lastActivity: new Date(createdChannel.data.lastActivity ?? ""),
    hasUnreadMessage: false,
    members,
  };
};

const extractUserIds = (channels: any[]): string[] => {
  const userIds = new Set<string>();
  
  channels.forEach((channel) => {
    channel.metadata?.users?.forEach((user: string) => {
      // Filtrar bots conhecidos para evitar erros no fetch de usuários
      if (user !== 'uptime-bot' &&
        user !== 'trucker-bot' &&
        user !== 'uptime_dfc35ad1-c58d-479d-b629-336a70d471a5') {
        userIds.add(user);
      }
    });
  });
  
  return Array.from(userIds);
};


const getUserDisplayName = (userId: string, users: any, usersRio: any): string => {
  const rio = usersRio?.data?.result?.find((rio: { userId: string }) => rio.userId === userId)?.displayName;
  const amity = users?.[userId];
  const metadata = amity?.metadata;
  
  if (metadata?.displayName) return metadata.displayName;
  if (metadata?.givenName) return `${metadata.givenName} ${metadata.familyName}`;
  return rio || '';
};


const createChannelPayload = (
  channel: any, 
  userId: string, 
  users: any, 
  usersRio: any
): IChannel => {
  const usersFiltered = channel.metadata?.users.filter((res: string) => res !== userId) || [];
  const chatName = channel.metadata?.chatName;
  
  const usersName = usersFiltered
    .map((user: string) => getUserDisplayName(user, users, usersRio))
    .join(',');
  
  let displayName: string;
  // Canal de pacotes: tag "packages" OU trucker-bot/uptime-bot em metadata.users OU displayName antigo
  const hasPackageBot = channel.metadata?.users?.some((u: string) =>
    u === 'uptime-bot' || u === 'trucker-bot'
  ) || false;
  const isPackageChannel = channel.tags?.includes("packages") || hasPackageBot || false;
  const isOldPackageChannel = channel.displayName === "Sistema de Pacotes";
  
  let profileImage: string;
  if (isPackageChannel || isOldPackageChannel) {
    if (hasPackageBot) {
      profileImage = VOLKINHO_AVATAR_URL;
      // Usar 'Volkinho' como displayName (mesma lógica do Chat.tsx)
      displayName = 'Freterico';
    } else {
      // Fallback: usar o displayName do canal ou chatName
      displayName = chatName || channel.displayName || "Sistema de Pacotes";
      profileImage = users?.[usersFiltered[0]]?.metadata?.imageProfile?.fileUrl || '';
    }
  } else {
    displayName = chatName || usersName;
    profileImage = users?.[usersFiltered[0]]?.metadata?.imageProfile?.fileUrl || '';
  }

  return {
    id: channel._id,
    lastActivity: new Date(channel.lastActivity),
    displayName: displayName,
    hasUnreadMessage: channel.subChannelsUnreadCount > 0,
    members: usersFiltered,
    profileImage: profileImage,
    isPackageChannel: isPackageChannel || isOldPackageChannel,
  } as IChannel;
};

const filterValidChannels = (channels: any[]): any[] => {
  return channels.filter(
    (channel: any) => {
      // Remover canais deletados
      if (channel.isDeleted === true || channel.isDeleted === "true" || channel.deletedAt) {
        return false;
      }
      
      // Para canais de pacotes (identificados pela tag "packages"), verificar apenas metadata.users
      const isPackageChannel = channel.tags?.includes("packages") || false;
      if (isPackageChannel) {
        // Verificar se tem pelo menos 2 membros (sistema + usuário)
        if (!channel?.metadata?.users || channel.metadata.users.length < 2) {
          return false;
        }
      }
      
      // Apenas canais community com membros válidos
      return (
        channel.type === "community" && 
        channel?.metadata?.users?.length > 0 &&
        !channel.isDeleted &&
        !channel.deletedAt
      );
    }
  );
};

export const listUserChannels = async (
  userId: string,
  setChannels: (channels: IChannel[]) => void,
  finishFetch: () => void
) => {
  let channels: IChannel[] = [];
  const usersRio = await getUsersFleetRio();

  ChannelRepository.getChannels({ membership: "member", limit: 100 }, async (channelRes) => {
    if (channelRes.error || !channelRes.data.length) {
      return finishFetch();
    }

    const activeChannels = channelRes.data.filter((channel: any) => {
      // Filtrar canais deletados
      if (channel.isDeleted === true || channel.isDeleted === "true" || channel.deletedAt) {
        return false;
      }
      // Para canais de pacotes (identificados pela tag "packages"), verificar se tem pelo menos 2 usuários (sistema + usuário)
      const isPackageChannel = channel.tags?.includes("packages") || false;
      if (isPackageChannel) {
        if (!channel?.metadata?.users || channel.metadata.users.length < 2) {
          return false;
        }
      }
      return true;
    });

    const channelsFiltered = filterValidChannels(activeChannels);
    const userIds = extractUserIds(channelsFiltered);

    if (userIds.length === 0) {
      return finishFetch();
    }

    try {
      const usersResponse = await UserRepository.getUserByIds(userIds);
      const users = usersResponse.data?.reduce((acc: any, item: any) => {
        acc[item.userId] = item;
        return acc;
      }, {});

      if (users && channelsFiltered.length > 0) {
        channelsFiltered.forEach((channel) => {
          const channelPayload = createChannelPayload(channel, userId, users, usersRio);
          channels.push(channelPayload);
        });
        
        setChannels(channels);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }

    finishFetch();
  });
};

export const createTextMessage = async (newMessage: string, channelId: string) => {
  const textMessage = {
    subChannelId: channelId,
    dataType: MessageContentType.TEXT,
    data: {
      text: newMessage,
    },
  };

  const { data: message } = await MessageRepository.createMessage(textMessage);

  return message;
};

const MAX_FILES_PER_UPLOAD = 5;

export const createFileMessages = async (
  files: File[],
  channelId: string,
  onProgress?: (percent: number) => void
) => {
  if (files.length === 0) return [];
  if (files.length > MAX_FILES_PER_UPLOAD) {
    throw new Error(`Máximo de ${MAX_FILES_PER_UPLOAD} arquivos por vez.`);
  }

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const { data: uploadedFiles } = await FileRepository.uploadFile(formData, onProgress);

  const messages = [];
  for (const uploaded of uploadedFiles) {
    const { data: message } = await MessageRepository.createMessage({
      subChannelId: channelId,
      dataType: MessageContentType.FILE,
      fileId: uploaded.fileId,
    });
    messages.push(message);
  }

  return messages;
};

export const getFileInfo = async (fileId: string) => {
  const { data: file } = await FileRepository.getFile(fileId);
  return file;
};

export const getFileUploadEnabled = async (): Promise<boolean> => {
  try {
    const response = await api.get('/v1/social/amity/chat/file-upload-enabled');
    return response.data === true || response.data?.enabled === true || response.data === 'true';
  } catch {
    return false;
  }
};

export const markAllMessageAsRead = async (channelId: string) => {
  await ChannelRepository.markAsRead(channelId);
};

export const updateUserName = async (userId: string, givenName?: string, familyName?: string, accountId?: string) => {
  try {
    UserRepository.getUser(userId, async (response) => {
      const user = response.data;
      const metadata = user?.metadata || {};
      if (metadata.givenName === givenName && metadata.familyName === familyName && metadata.rioId === accountId)
        return;
      await UserRepository.updateUser(userId, {
        metadata: {
          ...metadata,
          givenName,
          familyName,
          rioId: accountId,
          accountId,
          displayName: "",
        },
      });
    });
  } catch (error) {
    console.error("Erro ao atualizar metadata:", error);
  }
};

